// export const serverurl = "http://localhost:3000"
export const serverurl = "https://directus-qa-support.azurewebsites.net"

const agentUrl = process.env.NEXT_PUBLIC_AGENT_URL
const agentId = Number(process.env.NEXT_PUBLIC_AGENT_ID)
const agentTeamId = process.env.NEXT_PUBLIC_AGENT_TEAM_ID

export interface AgentReference {
  link: string
  title: string
  origin: string
}

import {
  parseAgentServiceFilters,
  formatAgentServiceResultsMessage,
  type AgentServiceFilters,
  type AgentServiceResultInfo,
} from "./agent-service-filters"

export type { AgentServiceFilters, AgentServiceResultInfo }

export interface SendSignpostAIServiceResultsParams {
  serviceCount: number
  language?: string
  services: AgentServiceResultInfo[]
}

export interface AgentResponse {
  response: string
  filters?: AgentServiceFilters
  language?: string
  references?: AgentReference[]
  risk_category_name?: string
  present_sources?: boolean
}

export const api = {

  // getCountry: (country: number) => getFromServer<Country>(`${serverurl}/country/${country}`),
  getServices: (country: number, since = 0) => getFromServer<Service[]>(`${serverurl}/services/${country}/${since}`),
  getService: (service: number) => getFromServer<Service>(`${serverurl}/service/${service}`),
  getProviders: (country: number) => getFromServer<Provider[]>(`${serverurl}/providers/${country}`),
  getCategories: () => getFromServer<Categories>(`${serverurl}/categories`),
  getBots: () => getFromServer<{ [index: number]: string }>(`${serverurl}/bots`),
  getArticles: (country: number) => getFromServer<{ [index: string]: ZendeskCategory }>(`${serverurl}/articles/${country}`),
  getArticle: (country: number, article: number) => getFromServer<ZendeskArticle>(`${serverurl}/article/${country}/${article}`),
  getAttachments: (country: number, article: number) => getFromServer<ZendeskArticleAttachment[]>(`${serverurl}/attachments/${country}/${article}`),



  getCountry(country: number) {

    let preview = ""

    try {
      let params = new URL(document.location.toString()).searchParams
      let name = params.get("preview")
      preview = name ? "/preview" : ""
    } catch (error) { }

    return getFromServer<Country>(`${serverurl}/country/${country}${preview}`)


  },

  async askbot(req: ChatMessage, tts: boolean, bots: { label: string, value: number, history: BotHistory[] }[]) {

    console.log("Request: ", bots)

    let answer: ChatMessage = {
      type: "bot",
      messages: [],
      question: req.message,
      tts
    }

    if (!bots.length) return answer

    for (const b of bots) {

      let a: ChatMessage = {}

      const breq = { ...req, id: b.value, history: b.history, audio: req.audio, tts }

      let options = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(breq)
      }

      try {
        a = await fetch(`${serverurl}/ai/`, options).then(r => r.json())
      } catch (error) {
        a.error = error?.toString() ?? "Error"
        console.log("Error Contacting Bot", error)
      }

      if (a.error) a.message = a.error

      a.type = "bot"
      a.id = b.value
      a.botName = b.label
      a.question = req.message
      a.audio = req.audio
      answer.messages.push(a)
    }

    return answer
  },

  async qualifyBot(r: BotQualification) {

    let options = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(r)
    }

    try {
      await fetch(`${serverurl}/qualifybot/`, options).then(r => r.json())
    } catch (error) {
      console.log("Error Scoring Bot", error)
    }

  },

  async askSignpostAI(uid: string, message: string, location?: { latitude: number; longitude: number }): Promise<AgentResponse> {
    const payload: Record<string, unknown> = {
      id: agentId,
      team_id: agentTeamId,
      uid,
    }

    if (location) {
      payload.message = `User location: ${location.latitude}, ${location.longitude}. ${message}`;
    } else {
      payload.message = message;
    }

    const options = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }

    try {
      const res = await fetch(agentUrl, options).then(r => r.json()) as Record<string, unknown>
      return parseAgentFetchResponse(res)
    } catch (error) {
      console.log("Error contacting agent", error)
      return { response: "Sorry, something went wrong. Please try again." }
    }
  },

  async sendSignpostAIServiceResults(
    uid: string,
    params: SendSignpostAIServiceResultsParams,
  ): Promise<AgentResponse> {
    const payload: Record<string, unknown> = {
      id: agentId,
      team_id: agentTeamId,
      uid,
      message: formatAgentServiceResultsMessage(params),
    }

    const options = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }

    try {
      const res = await fetch(agentUrl, options).then(r => r.json()) as Record<string, unknown>
      return parseAgentFetchResponse(res)
    } catch (error) {
      console.log("Error sending agent service results", error)
      return { response: "" }
    }
  },



}

function parseAgentFetchResponse(res: Record<string, unknown>): AgentResponse {
  const parsed = isStructuredAgentResponse(res.response)
    ? parseAgentServiceFilters(res.response)
    : undefined

  const textResponse =
    typeof res.message === "string"
      ? res.message
      : typeof res.response === "string"
        ? res.response
        : ""

  return {
    response: textResponse,
    filters: parsed?.filters,
    language: parsed?.language,
    references: (res.sources ?? res.references) as AgentReference[] | undefined,
    risk_category_name:
      typeof res.risk_category_name === "string"
        ? res.risk_category_name
        : undefined,
    present_sources: Boolean(res.present_sources),
  }
}

function isStructuredAgentResponse(
  response: unknown,
): response is Record<string, unknown> {
  return response != null && typeof response === "object" && !Array.isArray(response)
}

async function getFromServer<T>(url: string): Promise<Awaited<T>> {
  let ret: any = null

  console.log("Loading from Server: ", url)

  try {
    ret = await fetch(url).then(r => r.json())
  } catch (error) {
    console.log(`Error Loading ${url}`, error)
  }

  console.log("Loaded from Server: ", ret)


  return ret
}



